/**
 * Pure helpers for the Allow List import / export UI (sc-11468): category
 * filtering in the manager, and the mapping edits the import preview makes.
 */
import type { ImportColumnMapping, ImportLayout, ImportMatch, ImportRowStatus } from './api';
import type { WordDictionaryEntry } from '../types';

/** Category filter values: every category, entries without one, or one category. */
export const ALL_CATEGORIES = '__all__';
export const NO_CATEGORY = '__none__';

/** Distinct categories, A–Z (case-insensitive). */
export function categoriesOf(entries: WordDictionaryEntry[]): string[] {
  const seen = new Set<string>();
  for (const entry of entries) {
    if (entry.category) seen.add(entry.category);
  }
  return [...seen].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

export function hasUncategorized(entries: WordDictionaryEntry[]): boolean {
  return entries.some((e) => !e.category);
}

export function filterByCategory(entries: WordDictionaryEntry[], filter: string): WordDictionaryEntry[] {
  if (filter === ALL_CATEGORIES) return entries;
  if (filter === NO_CATEGORY) return entries.filter((e) => !e.category);
  return entries.filter((e) => e.category === filter);
}

export const STATUS_LABELS: Record<ImportRowStatus, string> = {
  new: 'New',
  update: 'Update',
  unchanged: 'Already on list',
  duplicate: 'Duplicate',
  conflict: 'On other list',
  error: 'Error',
};

/** Switch the layout, keeping what still applies and resetting the rest. */
export function withLayout(mapping: ImportColumnMapping, layout: ImportLayout): ImportColumnMapping {
  if (layout === mapping.layout) return mapping;
  if (layout === 'columns_per_category') {
    return { ...mapping, layout, has_header: true, category_columns: [], match_by_category: {} };
  }
  return {
    ...mapping,
    layout,
    phrase_column: mapping.phrase_column ?? 0,
    category_column: null,
    match_column: null,
    match_by_category: {},
  };
}

/** Set the phrase / category / match column; a column can only play one part. */
export function withColumn(
  mapping: ImportColumnMapping,
  role: 'phrase_column' | 'category_column' | 'match_column',
  index: number | null
): ImportColumnMapping {
  const next: ImportColumnMapping = { ...mapping, [role]: index };
  if (index !== null) {
    for (const other of ['phrase_column', 'category_column', 'match_column'] as const) {
      if (other !== role && next[other] === index) next[other] = null;
    }
  }
  if (role === 'category_column') next.match_by_category = {};
  return next;
}

/** Include / exclude one column of a one-column-per-category file.
 *  An empty `category_columns` means "every column with a header". */
export function withCategoryColumn(
  mapping: ImportColumnMapping,
  allColumns: number[],
  index: number,
  include: boolean
): ImportColumnMapping {
  const current = mapping.category_columns.length ? mapping.category_columns : allColumns;
  const next = include
    ? [...new Set([...current, index])].sort((a, b) => a - b)
    : current.filter((c) => c !== index);
  // [] would mean "every column" again: keep at least one.
  if (!next.length) return mapping;
  return { ...mapping, category_columns: next };
}

/** Match type for one category; `null` is the uncategorised default. */
export function withCategoryMatch(
  mapping: ImportColumnMapping,
  category: string | null,
  match: ImportMatch
): ImportColumnMapping {
  if (category === null) return { ...mapping, default_match: match };
  return { ...mapping, match_by_category: { ...mapping.match_by_category, [category]: match } };
}

/** Save a Blob as a file download. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
